import { INewQuestionOption } from "@/@types";
import {
  useCreateOption,
  useRemoveOption,
  useUpdateOption,
} from "@/services/assessments/options";
import { useFormLesson } from "@/stores/form-exam";
import { Cancel, Delete, Save, Settings } from "@mui/icons-material";
import { Checkbox, IconButton, TextField } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

interface Props {
  option: INewQuestionOption;
  index: number;
  enableDeleteOption?: boolean;
}

export function InputOption({
  index,
  option,
  enableDeleteOption = false,
}: Props) {
  console.log("=== InputOption rendered ===");
  console.log("index:", index);
  console.log("option:", option);
  console.log("enableDeleteOption:", enableDeleteOption);

  // Refs para controlar estado do componente
  const isMountedRef = useRef(true);
  const currentOptionIdRef = useRef<string>(option.id);

  // Se a opção é local (nova), começa em modo de edição
  const [isEditing, setIsEditing] = useState(option.isLocal || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [description, setDescription] = useState(option.description || "");
  const [isCorrect, setIsCorrect] = useState(option.isCorrect || false);

  console.log("=== InputOption state ===");
  console.log("isEditing:", isEditing);
  console.log("isSubmitting:", isSubmitting);
  console.log("description:", description);
  console.log("isCorrect:", isCorrect);
  console.log("isMountedRef.current:", isMountedRef.current);

  const [setOption, loading, deleteOption] = useFormLesson((state) => [
    state.setOption,
    state.loading,
    state.deleteOption,
  ]);

  const createOption = useCreateOption();
  const updateOption = useUpdateOption();
  const removeOption = useRemoveOption();

  // Cleanup quando componente desmonta
  useEffect(() => {
    console.log("🔄 InputOption mounted, isMountedRef set to true");
    isMountedRef.current = true;

    return () => {
      console.log("🔄 InputOption unmounting, isMountedRef set to false");
      isMountedRef.current = false;
    };
  }, []);

  // Atualizar ref quando a opção muda
  useEffect(() => {
    currentOptionIdRef.current = option.id;
  }, [option.id]);

  // Atualiza o estado local quando a opção muda
  useEffect(() => {
    console.log("=== InputOption option changed ===");
    console.log("option:", option);
    console.log("isMountedRef.current:", isMountedRef.current);
    console.log("currentOptionIdRef.current:", currentOptionIdRef.current);
    console.log("isEditing:", isEditing);

    if (isMountedRef.current && currentOptionIdRef.current === option.id) {
      // Só atualizar se não estiver em modo de edição
      // Isso evita que o input seja desabilitado quando o usuário está editando
      if (!isEditing) {
        setDescription(option.description || "");
        setIsCorrect(option.isCorrect || false);
        // Se a opção é local, mantém em modo de edição
        setIsEditing(option.isLocal || false);
        console.log("✅ Updated local state for option:", option.id);
      } else {
        console.log("🔄 Skipped state update - currently editing");
      }
    } else {
      console.log(
        "❌ Skipped state update - component unmounted or option changed"
      );
    }
  }, [option, isEditing]);

  const handleCheckboxChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      console.log("=== handleCheckboxChange called ===");
      console.log("isMountedRef.current:", isMountedRef.current);
      console.log("isEditing:", isEditing);

      const { checked } = event.target;
      console.log("checked:", checked);

      // Se está marcando como correta, desmarca as outras opções da mesma questão
      if (checked) {
        const allOptions = useFormLesson.getState().options;
        allOptions?.forEach((opt) => {
          if (opt.questionId === option.questionId && opt.id !== option.id) {
            setOption(opt.id, { isCorrect: false });
          }
        });
      }

      // Atualiza o estado local
      setIsCorrect(checked);

      // Atualizar o store apenas se não estiver em modo de edição
      // Isso evita loops infinitos e mantém o modo de edição
      if (!isEditing) {
        setOption(option.id, { isCorrect: checked });
      }

      console.log("✅ Checkbox updated locally");
    },
    [option.questionId, option.id, setOption, isEditing]
  );

  const handleDescriptionChange = useCallback(
    (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      console.log("=== handleDescriptionChange called ===");
      console.log("isMountedRef.current:", isMountedRef.current);
      console.log("isEditing:", isEditing);

      const { value } = event.currentTarget;
      console.log("value:", value);

      // Atualizar apenas o estado local durante a edição
      setDescription(value);

      // Atualizar o store apenas se não estiver em modo de edição
      // Isso evita loops infinitos e mantém o modo de edição
      if (!isEditing) {
        setOption(option.id, { description: value });
      }

      console.log("✅ Description updated locally");
    },
    [option.id, setOption, isEditing]
  );

  const handleSave = useCallback(async () => {
    console.log("=== handleSave called ===");
    console.log("isMountedRef.current:", isMountedRef.current);
    console.log("currentOptionIdRef.current:", currentOptionIdRef.current);
    console.log("option.id:", option.id);

    if (!description.trim()) {
      toast.error("A descrição da opção é obrigatória");
      return;
    }

    setIsSubmitting(true);
    try {
      if (option.isLocal) {
        // Criar nova opção
        const result = await createOption.mutateAsync({
          questionId: option.questionId,
          description: description.trim(),
          isCorrect,
        });

        setOption(option.id, {
          ...option,
          description: description.trim(),
          isCorrect,
          id: result.id, // Atualizar com o ID real do backend
          isLocal: false, // Marcar como não local após salvar
        });
        toast.success("Opção criada com sucesso!");
        setIsEditing(false);
      } else {
        // Atualizar opção existente
        await updateOption.mutateAsync({
          id: option.id,
          description: description.trim(),
          isCorrect,
        });

        setOption(option.id, {
          ...option,
          description: description.trim(),
          isCorrect,
        });
        toast.success("Opção atualizada com sucesso!");
        setIsEditing(false);
      }
    } catch (error) {
      toast.error("Erro ao salvar opção");
      console.error("Erro ao salvar opção:", error);
    } finally {
      setIsSubmitting(false);
    }
  }, [option, description, isCorrect, createOption, updateOption, setOption]);

  const handleDelete = useCallback(async () => {
    console.log("=== handleDelete called ===");
    console.log("isMountedRef.current:", isMountedRef.current);

    if (option.isLocal) {
      // Se é nova, apenas remover do estado local
      deleteOption(option.id);
      return;
    }

    try {
      await removeOption.mutateAsync(option.id);
      deleteOption(option.id);
      toast.success("Opção removida com sucesso!");
    } catch (error) {
      toast.error("Erro ao remover opção");
      console.error("Erro ao remover opção:", error);
    }
  }, [option, removeOption, deleteOption]);

  const handleCancel = useCallback(() => {
    console.log("=== handleCancel called ===");
    console.log("isMountedRef.current:", isMountedRef.current);

    if (option.isLocal) {
      // Se é nova e cancelar, remover a opção
      deleteOption(option.id);
    } else {
      // Restaurar valores originais e sair do modo edição
      setDescription(option.description || "");
      setIsCorrect(option.isCorrect || false);
      setIsEditing(false);
    }
  }, [option, deleteOption]);

  const handleEdit = useCallback(() => {
    console.log("=== handleEdit called ===");
    console.log("isMountedRef.current:", isMountedRef.current);

    setIsEditing(true);
    console.log("✅ Edit mode enabled");
  }, []);

  // Estados computados
  const isCurrentlyLoading = loading || isSubmitting;
  const isDisabled = isCurrentlyLoading;

  return (
    <div className="gap-2 flex items-center">
      <Checkbox
        checked={isCorrect}
        onChange={handleCheckboxChange}
        disabled={isDisabled}
      />

      {isEditing ? (
        <TextField
          autoFocus
          required
          label={`Option ${index}`}
          value={description}
          onChange={handleDescriptionChange}
          disabled={isDisabled}
          fullWidth
          placeholder="Digite a descrição da opção..."
        />
      ) : (
        <TextField
          required
          label={`Option ${index}`}
          value={description}
          disabled
          fullWidth
        />
      )}

      <div className="flex items-center gap-1">
        {isEditing ? (
          <>
            <IconButton
              onClick={handleSave}
              color="primary"
              disabled={isDisabled}
              size="small"
            >
              <Save />
            </IconButton>
            <IconButton
              onClick={handleCancel}
              color="primary"
              disabled={isDisabled}
              size="small"
            >
              <Cancel />
            </IconButton>
          </>
        ) : (
          <>
            <IconButton
              onClick={handleEdit}
              color="primary"
              disabled={isDisabled}
              size="small"
            >
              <Settings />
            </IconButton>
            {enableDeleteOption && (
              <IconButton
                onClick={handleDelete}
                color="primary"
                disabled={isDisabled}
                size="small"
              >
                <Delete />
              </IconButton>
            )}
          </>
        )}
      </div>
    </div>
  );
}
